/**
 * Botones de transición de estado del placement.
 * State machine: programado → finalizado → realizado / cancelado.
 */
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { DateTimePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { media, type PlacementState } from "@/lib/api";

const NEXT_STATES: Record<PlacementState, PlacementState[]> = {
  programado: ["finalizado", "realizado", "cancelado"],
  finalizado: ["realizado", "cancelado"],
  realizado: [],
  cancelado: [],
};

const LABEL: Record<PlacementState, string> = {
  programado: "Programar",
  finalizado: "Marcar finalizado",
  realizado: "Marcar realizado",
  cancelado: "Cancelar",
};

export function PlacementStateTransition({ placementId, currentState }: { placementId: number; currentState: PlacementState }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState<PlacementState | null>(null);
  const [actualEnd, setActualEnd] = useState("");
  const [notes, setNotes] = useState("");

  const transition = useMutation({
    mutationFn: (target: PlacementState) => media.transitionPlacement(placementId, {
      state: target,
      actual_end_at: actualEnd || undefined,
      notes: notes || undefined,
    }),
    onSuccess: (_, target) => {
      qc.invalidateQueries({ queryKey: ["media", "placements"] });
      qc.invalidateQueries({ queryKey: ["media", "placement", placementId] });
      qc.invalidateQueries({ queryKey: ["media", "campaign-summary"] });
      toast.success(`Estado cambiado a ${target}`);
      setOpen(null);
      setActualEnd("");
      setNotes("");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const next = NEXT_STATES[currentState] ?? [];
  if (!next.length) return <div className="text-xs text-ink-400">Sin transiciones disponibles</div>;

  return (
    <div className="flex flex-wrap gap-2">
      {next.map(target => (
        <Dialog key={target} open={open === target} onOpenChange={(o) => setOpen(o ? target : null)}>
          <DialogTrigger asChild>
            <Button
              size="sm"
              variant={target === "cancelado" ? "destructive" : (target === "realizado" ? "default" : "outline")}
            >
              {LABEL[target]}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cambiar a {target}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {target === "realizado" && (
                <div>
                  <Label className="text-xs">Fecha real de fin</Label>
                  <DateTimePicker value={actualEnd} onChange={(e) => setActualEnd(e.target.value)} />
                </div>
              )}
              <div>
                <Label className="text-xs">Notas (opcional)</Label>
                <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones..." />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(null)}>Cancelar</Button>
              <Button
                onClick={() => transition.mutate(target)}
                disabled={transition.isPending}
              >
                Confirmar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ))}
    </div>
  );
}
